/* Persistent app state: user settings and progress, stored together in localStorage.
   The storage key and shape are unchanged from v1 so existing progress keeps working. */
import { Emitter } from '../events';
import { debounce } from '../util';

export type NoteNaming = 'both' | 'solfege' | 'letter';
export type Accidental = 'sharp' | 'flat';
export type Instrument = 'guitar' | 'piano' | 'sine';
export type ThemeSetting = 'system' | 'light' | 'dark';
export type OctaveMode = 'flex' | 'strict';

export interface Settings {
  noteNames: NoteNaming;
  accidental: Accidental;
  a4: number;
  instrument: Instrument;
  volume: number;
  theme: ThemeSetting;
  octaveMode: OctaveMode;
  headphones: boolean;
  micDevice: string;
  /** 0.5 .. 2 */
  micSensitivity: number;
  /** Comfortable vocal range as MIDI notes. */
  voiceLow: number | null;
  voiceHigh: number | null;
  voiceType: string | null;
  dailyGoal: number;
  showKeyHint: boolean;
}

export interface LevelState {
  stars: number;
  acc: number;
  best: number;
  plays: number;
  last?: number;
}

export interface Progress {
  xp: number;
  /** XP per local day (YYYY-MM-DD). */
  days: Record<string, number>;
  /** "module.lessonId" -> completion time. */
  lessons: Record<string, number>;
  levels: Record<string, LevelState>;
  /** Free-form records: song transcription state, metronome BPM, breath records... */
  records: Record<string, unknown>;
  badges: Record<string, number>;
  modXp: Record<string, number>;
  created: number;
  sessions: number;
}

export const DEFAULT_SETTINGS: Settings = {
  noteNames: 'both',
  accidental: 'sharp',
  a4: 440,
  instrument: 'guitar',
  volume: 0.8,
  theme: 'system',
  octaveMode: 'flex',
  headphones: false,
  micDevice: '',
  micSensitivity: 1,
  voiceLow: null,
  voiceHigh: null,
  voiceType: null,
  dailyGoal: 60,
  showKeyHint: true,
};

const freshProgress = (): Progress => ({
  xp: 0,
  days: {},
  lessons: {},
  levels: {},
  records: {},
  badges: {},
  modXp: {},
  created: Date.now(),
  sessions: 0,
});

export const STORAGE_KEY = 'kulaktan.v1';

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function load(): { settings: Settings; progress: Progress } {
  let raw: { settings?: Partial<Settings>; progress?: Partial<Progress> } | null = null;
  try {
    raw = JSON.parse(storage()?.getItem(STORAGE_KEY) || 'null');
  } catch {
    raw = null;
  }
  return {
    settings: { ...DEFAULT_SETTINGS, ...raw?.settings },
    progress: { ...freshProgress(), ...raw?.progress },
  };
}

const initial = load();
let settings: Settings = initial.settings;
let progress: Progress = initial.progress;
let settingsVersion = 0;
let progressVersion = 0;

export interface StoreEvents {
  settings: { key: keyof Settings; val: unknown };
  progress: { badge?: string; xp?: number; level?: string };
}
export const storeEvents = new Emitter<StoreEvents>();

export const persist = debounce(() => {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify({ settings, progress }));
  } catch {
    /* storage unavailable (private mode, quota) */
  }
}, 250);

export const getSettings = (): Settings => settings;
export const getProgress = (): Progress => progress;
export const getSettingsVersion = (): number => settingsVersion;
export const getProgressVersion = (): number => progressVersion;

export function setSetting<K extends keyof Settings>(key: K, val: Settings[K]): void {
  settings = { ...settings, [key]: val };
  settingsVersion++;
  persist();
  storeEvents.emit('settings', { key, val });
}

/** Mutate progress in place, then notify subscribers once. */
export function updateProgress(fn: (p: Progress) => void, detail: StoreEvents['progress'] = {}): void {
  fn(progress);
  progressVersion++;
  persist();
  storeEvents.emit('progress', detail);
}

export function resetProgress(): void {
  progress = freshProgress();
  progressVersion++;
  persist();
  storeEvents.emit('progress', {});
}

export const getRecord = <T>(key: string): T | undefined => progress.records[key] as T | undefined;

export function setRecord(key: string, value: unknown): void {
  updateProgress((p) => {
    if (value === undefined) delete p.records[key];
    else p.records[key] = value;
  });
}

export const subscribeSettings = (fn: () => void): (() => void) => storeEvents.on('settings', fn);
export const subscribeProgress = (fn: () => void): (() => void) => storeEvents.on('progress', fn);
